// DAY-DIRECTSTAFF-2026-10-24 §1: request -> offer in one step. On a facility request, a presented card the facility marked
// Interested gets "Make offer": staff say which candidate the card is (a card carries initials only -- nothing about a candidate
// ever goes to a facility, so the link is made here, by staff), and that candidate's Offers form opens pre-filled from the
// request: discipline, setting, city/state, start, length. The facility's bill rate is shown to staff as context only and never
// copied into the pay lines. Making the offer writes the facility-side timeline line, which is also the offer's link to the card:
//   "Offer 12 made for card 100 (request 7) · AB · lead_id=<uuid>"      (activity_log, type facility_portal)
// Accepting the offer then sets the card to Placed (facility_staff_card_status) and the request to filled
// (facility_staff_set_status for a portal request; the website request's own pipeline status otherwise), with a line on both sides.
import { PORTAL_LOG_TYPE, normInitials, rateText } from './facilityPortal';

const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const DISCIPLINES = ['PT', 'PTA', 'OT', 'COTA', 'SLP', 'SLPA'];

/** "Ada Byron Lovelace" -> ['AL', 'ABL'] (first + last, and every word) */
export function initialsOf(name) {
  const w = s(name).split(/\s+/).filter((x) => /^[A-Za-z]/.test(x)).map((x) => x[0].toUpperCase());
  if (!w.length) return [];
  return [...new Set([w.length > 1 ? `${w[0]}${w[w.length - 1]}` : w[0], w.join('')])];
}
/** The candidates the card may be: initials and discipline match first, then initials only; [] when none */
export function candidatesForCard(card, candidates = []) {
  const ini = normInitials(card && card.initials);
  if (!ini) return [];
  const disc = s(card.discipline).toUpperCase();
  const hit = (candidates || []).filter((c) => initialsOf(c.name).includes(ini));
  return [...hit.filter((c) => s(c.specialty).toUpperCase() === disc), ...hit.filter((c) => s(c.specialty).toUpperCase() !== disc)];
}
/** The offer form's start: the request's job (never its rate) + the card it is for */
export function draftFromRequest({ request = null, f = {}, card, leadId = null }) {
  const r = request || {};
  const disc = s(r.discipline || card.discipline || f.discipline).toUpperCase();
  return {
    form: { facility_request_id: request ? String(request.id) : '', facility: request ? '' : s(f.facility), discipline: DISCIPLINES.includes(disc) ? disc : '',
      setting: s(r.setting), city: s(r.city), state: s(r.state), start_date: s(r.start_date).slice(0, 10), length_weeks: r.length_weeks ? String(r.length_weeks) : '13',
      pay_hourly_taxable: '', pay_weekly_stipend: '', pay_other: '', notes: '' },
    card: { id: card.id, initials: normInitials(card.initials), request_id: card.request_id || null },
    requestId: request ? request.id : null, leadId: leadId || null, facility: s(f.facility),
    rateContext: request && rateText(request) ? `${rateText(request)} quoted by the facility` : request && 'bill_rate' in request ? 'No rate from the facility yet' : '',
  };
}

const LINK_RE = /^Offer (\d+) made for card (\d+) \((?:request (\d+)|website request)\) · ([A-Z]{1,3})(?: · lead_id=([0-9a-f-]{36}))?$/;
export const linkNote = (offerId, d) => `Offer ${offerId} made for card ${d.card.id} (${d.requestId ? `request ${d.requestId}` : 'website request'}) · ${d.card.initials}${d.leadId ? ` · lead_id=${d.leadId}` : ''}`;
export function parseLinkNote(note) {
  const m = LINK_RE.exec(s(note));
  return m ? { offerId: Number(m[1]), cardId: Number(m[2]), requestId: m[3] ? Number(m[3]) : null, initials: m[4], leadId: m[5] || null } : null;
}
export const acceptedNote = (offerId, link, done) => `Offer ${offerId} accepted · card ${link.initials} ${done.card ? 'placed' : 'NOT placed'} · ${link.requestId ? `request ${link.requestId}` : 'website request'} ${done.request ? 'filled' : 'NOT filled'}${link.leadId ? ` · lead_id=${link.leadId}` : ''}`;
const ACC_RE = /^Offer (\d+) accepted · card ([A-Z]{1,3}) (placed|NOT placed) · (request \d+|website request) (filled|NOT filled)(?: · lead_id=)?/;
/** The facility lead's timeline words for these two lines -> { title, line, tone } | null */
export function portalOfferEntry(note) {
  const l = parseLinkNote(note);
  if (l) return { title: 'Offer made', line: `For card ${l.initials}${l.requestId ? ` · request ${l.requestId}` : ''}` };
  const a = ACC_RE.exec(s(note));
  if (a) return { title: 'Offer accepted', line: `Card ${a[2]} ${a[3]} · ${a[4]} ${a[5]}`, tone: a[3] === 'placed' && a[5] === 'filled' ? 'good' : 'bad' };
  return null;
}

/** supabase + the portal api + the website request's own status save */
export function makeRequestOfferApi({ supabase, portalApi, saveLeadStatus = null }) {
  const log = async (note) => {
    const { error } = await supabase.from('activity_log').insert({ candidate_id: null, date: new Date().toISOString().slice(0, 10), type: PORTAL_LOG_TYPE, note });
    if (error) throw new Error(`Not logged on the facility's timeline: ${error.message}`);
  };
  return {
    /** after offer_create: the facility-side line, which is also the link to the card */
    linked: (offer, draft) => log(linkNote(offer.id, draft)),
    /** -> the link for this offer, or null (an offer not made from a card) */
    async linkFor(offerId) {
      const { data, error } = await supabase.from('activity_log').select('note').eq('type', PORTAL_LOG_TYPE).like('note', `Offer ${Number(offerId)} made for card %`).limit(1);
      if (error) return null;
      return parseLinkNote(((data || [])[0] || {}).note);
    },
    /** after the offer is accepted: the card Placed, the request filled, a line; -> { card, request, words } (never throws) */
    async accepted(offer) {
      const link = await this.linkFor(offer.id);
      if (!link) return null;
      const done = { card: false, request: false }; const why = [];
      try { await portalApi.cardStatus(link.cardId, 'placed'); done.card = true; } catch (e) { why.push(e.message); }
      try {
        if (link.requestId) { await portalApi.setStatus(link.requestId, 'filled'); done.request = true; }
        else if (link.leadId && saveLeadStatus) { await saveLeadStatus(link.leadId, { status: 'filled' }); done.request = true; }
        else why.push('The website request has no pipeline status to set.');
      } catch (e) { why.push(e.message); }
      try { await log(acceptedNote(offer.id, link, done)); } catch (e) { why.push(e.message); }
      return { ...done, words: done.card && done.request ? `Card ${link.initials} set to Placed and the request to filled` : `Accepted. On the facility side: ${why.join(' ')}` };
    },
  };
}
